export type CallPhase =
  | 'idle'
  | 'loading_sdk'
  | 'requesting_permissions'
  | 'preview_ready'
  | 'joining'
  | 'joined'
  | 'reconnecting'
  | 'left'
  | 'ended'
  | 'denied'
  | 'expired'
  | 'revoked'
  | 'locked'
  | 'device_error'

export interface CallParticipant {
  sessionId: string
  name: string
  isLocal: boolean
  audioOn: boolean
  videoOn: boolean
  screenOn: boolean
  isActiveSpeaker: boolean
  /** Mirrors the Daily token's is_owner claim (server-derived — see
   *  api/_lib/meetings/service.ts issueJoinToken). Read-only display signal,
   *  never a client-side permission decision. */
  isOwner: boolean
  videoTrack?: MediaStreamTrack
  audioTrack?: MediaStreamTrack
  screenTrack?: MediaStreamTrack
}

export interface LocalMedia {
  micOn: boolean
  camOn: boolean
  screenOn: boolean
}

export interface CallSnapshot {
  phase: CallPhase
  participants: CallParticipant[]
  local: LocalMedia
  message: string | null
  networkWarning: boolean
  errorCode?: string
}

export interface MediaDevice {
  deviceId: string
  label: string
  kind: 'videoinput' | 'audioinput' | 'audiooutput'
}

export interface ClientCallProvider {
  subscribe(callback: (snapshot: CallSnapshot) => void): () => void
  getSnapshot(): CallSnapshot
  preview(): Promise<void>
  join(): Promise<void>
  setMic(on: boolean): Promise<void>
  setCam(on: boolean): Promise<void>
  setScreen(on: boolean): Promise<void>
  leave(): Promise<void>
  /** Device menu — optional; the real Daily provider implements them, the
   *  test/preview provider does not (a room degrades to no device switching). */
  listDevices?(): Promise<MediaDevice[]>
  setCamera?(deviceId: string): Promise<void>
  setMicrophone?(deviceId: string): Promise<void>
  setSpeaker?(deviceId: string): Promise<void>
}

export interface CallProviderInput {
  roomUrl: string
  token: string
  displayName?: string
}

export type CallProviderFactory = (input: CallProviderInput) => ClientCallProvider

export const IDLE_CALL_SNAPSHOT: CallSnapshot = {
  phase: 'idle',
  participants: [],
  local: { micOn: false, camOn: false, screenOn: false },
  message: null,
  networkWarning: false,
}
